import { z } from "zod";

/**
 * ADR-0015 section 11: what the agent extension posts, every five
 * seconds, to the endpoint the customer's stack creates. Every field is
 * a number about the connection or the machine, a device label, or an
 * id; the schema is strict, so a field the extension has not declared is
 * refused rather than stored. The extension keeps its own copy of these
 * shapes (it has no dependencies); a change here is a change there.
 */
const Fraction = z.number().min(0).max(1);
const Ms = z.number().min(0).max(60_000);

export const RtcSampleSchema = z
  .object({
    jitterMs: Ms.optional(),
    lossIn: Fraction.optional(),
    lossOut: Fraction.optional(),
    rttMs: Ms.optional(),
    jitterBufferMs: Ms.optional(),
    concealed: z.number().int().nonnegative().optional(),
    concealmentEvents: z.number().int().nonnegative().optional(),
    levelIn: Fraction.optional(),
    levelOut: Fraction.optional(),
    candidate: z.enum(["host", "srflx", "prflx", "relay", "unknown"]).optional(),
    network: z.string().max(32).optional(),
  })
  .strict();

export const MachineSampleSchema = z
  .object({
    cpu: Fraction.optional(),
    memory: Fraction.optional(),
    visible: z.boolean(),
    longTasks: z.number().int().nonnegative(),
    devices: z.object({ in: z.string().max(128).optional(), out: z.string().max(128).optional() }).strict().optional(),
    deviceChanges: z.number().int().nonnegative(),
  })
  .strict();

export const TelemetrySampleSchema = z
  .object({
    ts: z.string().datetime({ offset: true }),
    v: z.string().min(1).max(32),
    agent: z.string().max(128),
    agentSource: z.enum(["streams", "page", "config", "unknown"]),
    session: z.string().min(1).max(64),
    contactId: z.string().max(64).optional(),
    rtc: RtcSampleSchema,
    machine: MachineSampleSchema,
  })
  .strict();
export type TelemetrySample = z.infer<typeof TelemetrySampleSchema>;

export const TelemetryBatchSchema = z
  .object({
    environmentId: z.string().max(64),
    extensionVersion: z.string().min(1).max(32),
    sentAt: z.string().datetime({ offset: true }),
    samples: z.array(TelemetrySampleSchema).min(1).max(1000),
  })
  .strict();
export type TelemetryBatch = z.infer<typeof TelemetryBatchSchema>;

/**
 * What the forwarder attaches to a contact record from the series in the
 * customer's account: the shape of the call's audio and the agent's
 * machine, never a second of it (requirements 8.7). Offsets are seconds
 * from the agent answering.
 */
const Stat = z.object({ p95: z.number(), max: z.number() }).strict();
export const AudioSummarySchema = z
  .object({
    samples: z.number().int().nonnegative(),
    sessions: z.number().int().nonnegative(),
    jitter_ms: Stat.optional(),
    loss_in: Stat.optional(),
    loss_out: Stat.optional(),
    rtt_ms: Stat.optional(),
    jitter_buffer_ms: Stat.optional(),
    concealment_events: Stat.optional(),
    cpu: Stat.optional(),
    memory: Stat.optional(),
    /** The worst ten-second windows, as offsets from the agent answering, with why. At most five. */
    worst_windows: z.array(z.object({ offset_seconds: z.number().int().nonnegative(), reasons: z.array(z.string().max(32)).max(6) }).strict()).max(5),
    turn: z.boolean(),
    headset: z.string().max(128).optional(),
    device_changes: z.number().int().nonnegative(),
    /** Seconds the softphone tab was hidden during the call. */
    hidden_seconds: z.number().int().nonnegative(),
    extension_version: z.string().max(32).optional(),
  })
  .strict();
export type AudioSummary = z.infer<typeof AudioSummarySchema>;

/**
 * #272, documentation/pricing.md: the price rule, in config, applied at
 * read time. `amount = max(floor, min(cap, rate × minute-equivalents))`
 * where a chat message, an email and a case each count as a fraction or
 * multiple of a voice minute.
 */
export interface PricingConfig {
  ratePerMinuteEquivalent: number;
  monthlyCap: number;
  monthlyFloor: number;
  equivalents: { chat: number; email: number; case: number };
}

export function minuteEquivalents(u: { voice_minutes: number; chat_messages: number; emails: number; cases: number }, p: PricingConfig): number {
  return u.voice_minutes + u.chat_messages * p.equivalents.chat + u.emails * p.equivalents.email + u.cases * p.equivalents.case;
}

export function priceFor(minuteEq: number, p: PricingConfig): number {
  return Math.round(Math.max(p.monthlyFloor, Math.min(p.monthlyCap, p.ratePerMinuteEquivalent * minuteEq)) * 100) / 100;
}

export interface UsageMonthView {
  month: string;
  source: "cost-explorer" | "connect-metrics";
  voice_minutes: number;
  chat_messages: number;
  emails: number;
  cases: number;
  minute_equivalents: number;
  as_of: string;
  complete: boolean;
  lines: Array<{ usage_type: string; quantity: number; unit?: string }>;
}

export interface UsageResponse {
  months: UsageMonthView[];
  pricing: PricingConfig;
}

/** The platform view's roll-up (internal): one row per organization for a month, distinct accounts summed, the amount the rule gives. */
export interface UsageRollupResponse {
  month: string;
  pricing: PricingConfig;
  organisations: Array<{
    organisationId: string;
    name: string;
    environments: Array<{ environmentId: string; name: string; live: boolean; awsAccountNumber: string; source?: "cost-explorer" | "connect-metrics"; minute_equivalents: number; reported: boolean }>;
    minute_equivalents: number;
    amount: number;
    /** No live environment reported this month: shown, never silently zero. */
    unreported: boolean;
  }>;
}
